import { prepareMessagesForChatStreamResume } from '@terra/ui-design-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  shouldResumeChatStreamAfterFinish,
  startChatStreamResume,
} from '@terra/shared-utils';

export type ChatInstanceRuntimeStatus =
  | 'streaming'
  | 'submitted'
  | 'ready'
  | 'error';

export const shouldReloadHistoryAfterFinish = ({
  finishEvent,
  wasResuming,
  hasQueuedFollowUps,
}: {
  finishEvent: { isAbort: boolean; isDisconnect: boolean; isError: boolean };
  wasResuming: boolean;
  hasQueuedFollowUps: boolean;
}): boolean =>
  (wasResuming || hasQueuedFollowUps) &&
  !finishEvent.isAbort &&
  !finishEvent.isDisconnect &&
  !finishEvent.isError;

export const handleChatInstanceFinish = <
  TFinishEvent extends {
    isAbort: boolean;
    isDisconnect: boolean;
    isError: boolean;
  },
>({
  finishEvent,
  intentionalAbort,
  isTerminalError = false,
  resumeStream,
  setChatStatus,
  onSessionComplete,
  onFinish,
  setStreamResuming,
  messages,
  setMessages,
  backgroundTurnHandoff = false,
  backgroundTurnResumeStarted = false,
  onResumedStreamSettled,
}: {
  finishEvent: TFinishEvent;
  intentionalAbort: boolean;
  isTerminalError?: boolean;
  resumeStream?: () => void | Promise<unknown>;
  setChatStatus: (status: ChatInstanceRuntimeStatus) => void;
  onSessionComplete: () => void;
  onFinish?: (finishEvent: TFinishEvent) => void;
  setStreamResuming?: (isResuming: boolean) => void;
  messages?: TerraUIMessage[];
  setMessages?: (messages: TerraUIMessage[]) => void;
  backgroundTurnHandoff?: boolean;
  backgroundTurnResumeStarted?: boolean;
  onResumedStreamSettled?: () => void;
}): void => {
  if (backgroundTurnHandoff) {
    if (!backgroundTurnResumeStarted) {
      setStreamResuming?.(true);
      setChatStatus('ready');
    }

    onFinish?.(finishEvent);

    return;
  }

  if (
    shouldResumeChatStreamAfterFinish({
      isAbort: finishEvent.isAbort,
      isDisconnect: finishEvent.isDisconnect,
      isError: finishEvent.isError,
      isTerminalError,
      intentionalAbort,
    })
  ) {
    setStreamResuming?.(true);

    if (messages && setMessages) {
      setMessages(prepareMessagesForChatStreamResume(messages));
    }

    startChatStreamResume({
      resumeStream,
      onResumeFailed: () => setStreamResuming?.(false),
    });
    onFinish?.(finishEvent);

    return;
  }

  if (!finishEvent.isError) {
    setStreamResuming?.(false);
  }

  const failedFinish =
    finishEvent.isAbort || finishEvent.isDisconnect || finishEvent.isError;

  setChatStatus(failedFinish ? 'error' : 'ready');

  if (!failedFinish) {
    onResumedStreamSettled?.();
    onSessionComplete();
  }

  onFinish?.(finishEvent);
};
